/**
 * Honest locked state (lib/content-lock.ts): when this browser cannot read the account's current
 * encrypted key, every surface says so — the tab strip's "Locked · Unlock" chip, and the
 * "Your work is locked in this browser" panel on Home and project tabs — instead of "0 projects".
 * Unlock is ONE passkey tap in place (lib/unlock-ceremony.ts): the passkey's PRF secret opens the
 * keys locally, and without PRF the same tap asks a key-holding machine ("Unlock via
 * Areks-Macbook-Pro.local"). No sign-out, and the lists refill without a reload. The app lock's
 * tap does the same, so a locked browser never needs a second tap. ⇧U unlocks from anywhere while
 * locked. Inline (never a bottom sheet).
 */
import { useQueryClient } from '@tanstack/react-query'
import { Link } from '@tanstack/react-router'
import { Button, shortcutLabel, shortcutProps, useShortcut } from '@viko/transcript-ui'
import {
  ChevronDown,
  ChevronRight,
  FileKey,
  Fingerprint,
  Laptop,
  Lock,
  Monitor,
  OctagonAlert,
  ScanFace,
  X,
} from 'lucide-react'
import { useEffect, useRef, useState, useSyncExternalStore } from 'react'
import { useBackend } from '../../lib/backend.js'
import type { ContentLockState } from '../../lib/content-lock.js'
import { homeHref } from '../../lib/ia.js'
import {
  deviceNoun,
  friendlyMachineName,
  type HolderView,
  holderView,
  loadLine,
  lockedView,
} from '../../lib/locked-view.js'
import { useMachines } from '../../lib/queries.js'
import { type MachineFailure, machineFailureText } from '../../lib/unlock-ceremony.js'
import { useInboxRows } from '../inbox/useInboxRows.js'
import { Glyph } from '../lanes/glyphs.js'
import './lock.css'

const ALWAYS_UNLOCKED: ContentLockState = { status: 'unlocked' }
const noop = () => () => {}

export function useContentLock(): ContentLockState {
  const b = useBackend()
  const lock = b.lock
  return useSyncExternalStore(
    lock?.subscribe ?? noop,
    lock?.get ?? (() => ALWAYS_UNLOCKED),
    lock?.get ?? (() => ALWAYS_UNLOCKED),
  )
}

/** Locked (or unlocking): encrypted counts are unknown here and render "—". */
export function useLocked(): boolean {
  const s = useContentLock().status
  return s === 'locked' || s === 'unlocking'
}

/** A count that is unknown while locked. */
export function lockedCount(locked: boolean, n: number): string {
  return locked ? '—' : String(n)
}

/** Unlock with the one passkey tap; errors land in the lock state (shown by the panel). */
export function useUnlock() {
  const b = useBackend()
  return () => {
    void b.lock?.unlock().catch(() => {})
  }
}

/**
 * Every content-derived query (machines + encrypted reports, projects, inbox, runs…) refetches
 * the moment content becomes readable, whatever path got there (PRF, machine, already unlocked).
 */
export function shouldRefetchAfterUnlock(
  was: ContentLockState['status'],
  now: ContentLockState['status'],
) {
  return now === 'unlocked' && was !== 'unlocked'
}

/**
 * Mounted once by the app shell: checks on open and whenever the page becomes visible again
 * (a rotation elsewhere locks an open tab), refetches everything once unlocked, and binds ⇧U.
 */
export function ContentLockWatcher() {
  const b = useBackend()
  const qc = useQueryClient()
  const state = useContentLock()
  const unlock = useUnlock()
  const previous = useRef(state.status)
  useEffect(() => {
    if (!b.lock) return
    void b.lock.check()
    const onVisible = () => {
      if (document.visibilityState === 'visible') void b.lock.check()
    }
    document.addEventListener('visibilitychange', onVisible)
    return () => document.removeEventListener('visibilitychange', onVisible)
  }, [b])
  useEffect(() => {
    const was = previous.current
    previous.current = state.status
    // Unlocked in place: every encrypted list refills now (no reload).
    if (shouldRefetchAfterUnlock(was, state.status)) void qc.invalidateQueries()
  }, [state.status, qc])
  useShortcut(
    'global.unlock',
    (e) => {
      e.preventDefault()
      unlock()
    },
    state.status === 'locked',
  )
  return null
}

/** The tab strip's affordance, right after Home. */
export function LockedTabChip() {
  const state = useContentLock()
  const unlock = useUnlock()
  if (state.status !== 'locked' && state.status !== 'unlocking') return null
  const busy = state.status === 'unlocking'
  const label = busy
    ? state.phase === 'machine' && state.machineName
      ? `Unlocking via ${friendlyMachineName(state.machineName)}…`
      : 'Unlocking…'
    : 'Locked · Unlock'
  return (
    <li className="lk-tab lk-locked">
      <button
        type="button"
        className="lk-locked__btn"
        onClick={unlock}
        disabled={busy}
        {...shortcutProps('global.unlock', 'Unlock your work in this browser')}
      >
        <Lock size={14} aria-hidden="true" />
        {busy ? (
          <span>{label}</span>
        ) : (
          <span>
            <span className="lk-locked__pre">Locked ·</span> Unlock
          </span>
        )}
        <kbd>{shortcutLabel('global.unlock')}</kbd>
      </button>
    </li>
  )
}

const FALLBACK_TEXT = {
  'no-prf':
    'This passkey can’t unlock encrypted work by itself, and none of your machines that hold your key answered.',
  'no-escrow': 'This passkey isn’t set up to unlock encrypted work yet.',
  'still-locked': 'Your passkey worked, but this browser still can’t read your newest key.',
} as const

/** One problem, in words; the raw code only behind "Details". */
function Problem({ text, code }: { text: string; code?: string | undefined }) {
  return (
    <div role="alert" className="lk-lockpanel__problem" data-testid="content-unlock-failure">
      <OctagonAlert size={16} aria-hidden="true" className="lk-lockpanel__probicon" />
      <div className="lk-lockpanel__probtext">
        <p>{text}</p>
        {code ? (
          <details className="lk-lockpanel__details">
            <summary>Details</summary>
            <code>{code}</code>
          </details>
        ) : null}
      </div>
    </div>
  )
}

function failureCode(f: MachineFailure): string {
  const parts = [`unlock: ${f.code}`]
  if (f.reason) parts.push(`reason: ${f.reason}`)
  if (f.detail && f.detail !== f.code) parts.push(`detail: ${f.detail}`)
  return parts.join(' · ')
}

/** "Areks MacBook Pro · Online" or "Offline · last seen 11 h ago", with what to do. */
function HolderRow({ h }: { h: HolderView }) {
  const Icon = h.noun === 'your PC' ? Monitor : Laptop
  return (
    <li className="lk-holder" data-online={h.online ? '1' : '0'} data-testid="unlock-holder">
      <span className="lk-holder__icon">
        <Icon size={16} aria-hidden="true" />
      </span>
      <div className="lk-holder__text">
        <p className="lk-holder__title">
          <span title={h.hostname}>{h.name}</span>
          <span className="lk-holder__state">
            <span className={h.online ? 'is-online' : 'is-offline'}>{h.state.split(' · ')[0]}</span>
            {h.state.includes(' · ') ? ` · ${h.state.split(' · ').slice(1).join(' · ')}` : null}
          </span>
        </p>
        {h.hint ? (
          <p className="lk-holder__hint">
            {h.hint} Still offline once it’s open? On it, run <code>viko service install</code>.
          </p>
        ) : null}
      </div>
    </li>
  )
}

/**
 * The locked-work panel: a short title and one sentence, the machine(s) that can unlock with
 * their live state, ONE primary action, a problem in words (codes behind "Details"), the other
 * ways folded under "Other ways", and what is still true while locked.
 */
export function LockedPanel({ where }: { where: 'home' | 'project' }) {
  const state = useContentLock()
  const unlock = useUnlock()
  const built = useInboxRows()
  const machines = useMachines()
  const view = lockedView(built.rows, machines.data ?? [])
  const [others, setOthers] = useState<boolean | undefined>(undefined)
  if (state.status !== 'locked' && state.status !== 'unlocking') return null
  const busy = state.status === 'unlocking'
  const locked = state.status === 'locked' ? state : undefined
  const now = Date.now()
  const platformOf = (id: string) => machines.data?.find((m) => m.id === id)?.platform
  const holders = (
    locked?.holders ??
    locked?.machines?.map((m) => ({ ...m, online: true })) ??
    []
  ).map((h) => holderView(h, platformOf(h.machineId), now))
  const lead = holders.find((h) => h.online) ?? holders[0]
  const device = deviceNoun()
  const Device = device.replace(/^this/, 'This')
  const primary = lead ? `Unlock with ${lead.noun}` : 'Unlock with passkey'
  const busyLabel =
    state.status === 'unlocking' && state.phase === 'machine' && state.machineName
      ? `Unlocking via ${friendlyMachineName(state.machineName)}…`
      : 'Waiting for passkey…'
  const failure = locked?.failure
  const fallback = locked?.fallback
  const problem = failure
    ? {
        // The machine rows already say offline and what to do: the problem stays one line.
        text:
          failure.code === 'no-machine' && lead
            ? lead.online
              ? `${lead.name} didn’t answer. Check that Viko is running on it, then unlock again.`
              : `Couldn’t reach ${lead.name}, so ${device} is still locked.`
            : machineFailureText({
                ...failure,
                ...(failure.machineName
                  ? { machineName: friendlyMachineName(failure.machineName) }
                  : holders.length === 1 && lead
                    ? { machineName: lead.name }
                    : {}),
              }),
        code: failureCode(failure),
      }
    : locked?.error
      ? { text: locked.error }
      : fallback && !(lead && fallback !== 'still-locked')
        ? { text: FALLBACK_TEXT[fallback], code: `unlock: ${fallback}` }
        : undefined
  // No machine can help: the other ways open by themselves.
  const othersOpen = others ?? holders.length === 0
  return (
    <section
      className="lk-lockpanel"
      aria-labelledby={`lock-${where}-h`}
      data-testid="content-lock"
    >
      <div className="lk-lockpanel__head">
        <span className="lk-lockpanel__icon">
          <Lock size={18} aria-hidden="true" />
        </span>
        <div className="lk-lockpanel__lead">
          <h2 id={`lock-${where}-h`} className="lk-lockpanel__title">
            Your work is locked
          </h2>
          <p className="lk-lockpanel__text">
            Your work is end-to-end encrypted.{' '}
            {lead
              ? `${Device} needs your key from ${lead.noun}: one passkey tap.`
              : `${Device} needs your key: one passkey tap.`}
            {where === 'project' ? ' This project appears once you unlock.' : ''}
          </p>
        </div>
      </div>
      {holders.length ? (
        <ul className="lk-holders" aria-label="Machines that hold your key">
          {holders.map((h) => (
            <HolderRow key={h.machineId} h={h} />
          ))}
        </ul>
      ) : null}
      <div className="lk-lockpanel__actions">
        <Button
          variant="primary"
          icon={<Fingerprint size={16} aria-hidden="true" />}
          onClick={unlock}
          disabled={busy}
          data-testid="content-unlock"
          {...shortcutProps('global.unlock', primary)}
        >
          {busy ? busyLabel : primary}
          <kbd className="lk-lockpanel__kbd">{shortcutLabel('global.unlock')}</kbd>
        </Button>
      </div>
      {busy && state.status === 'unlocking' && state.phase === 'machine' ? (
        <p className="lk-lockpanel__note" role="status">
          Passkey OK. {friendlyMachineName(state.machineName ?? 'Your Mac')} is checking it and
          sending your key.
        </p>
      ) : null}
      {problem && !busy ? <Problem text={problem.text} code={problem.code} /> : null}
      <div className="lk-lockpanel__others">
        <button
          type="button"
          className="lk-lockpanel__toggle"
          aria-expanded={othersOpen}
          aria-controls={`lock-${where}-ways`}
          onClick={() => setOthers(!othersOpen)}
        >
          <ChevronDown size={16} aria-hidden="true" className="lk-lockpanel__chev" />
          Other ways
        </button>
        {othersOpen ? (
          <ul id={`lock-${where}-ways`} className="lk-ways">
            <li className="lk-way">
              <span className="lk-way__icon">
                <FileKey size={16} aria-hidden="true" />
              </span>
              <div className="lk-way__text">
                <Link
                  to={'/settings/security' as never}
                  hash="recovery-keys"
                  className="lk-way__title lk-way__link"
                >
                  Use your recovery kit
                </Link>
                <p className="lk-way__meta">Another signed-in device can approve it at once.</p>
              </div>
            </li>
            <li className="lk-way">
              <span className="lk-way__icon">
                <ScanFace size={16} aria-hidden="true" />
              </span>
              <div className="lk-way__text">
                <p className="lk-way__title">Set up Face ID unlock from a trusted browser</p>
                <p className="lk-way__meta">
                  Where your work shows (on your Mac): You › Security › Protect with passkey. Then
                  unlock here again.
                </p>
              </div>
            </li>
          </ul>
        ) : null}
      </div>
      {view.machines.length || view.folded.length ? (
        <div className="lk-lockpanel__still">
          <h3 className="lk-lockpanel__sub">Still true while locked</h3>
          <ul className="lk-facts">
            {view.folded.map((r) => (
              <li key={r.key}>
                <Link
                  to={homeHref({ v: 'needs', i: r.key })}
                  className="lk-fact"
                  aria-label={`${r.title}. Its details are in an encrypted machine report: unlock to read it.`}
                >
                  <span className="lk-fact__icon">
                    <Glyph kind="blocked" />
                  </span>
                  <span className="lk-fact__title">{r.title}</span>
                  <ChevronRight size={16} aria-hidden="true" className="lk-fact__chev" />
                </Link>
              </li>
            ))}
            {view.machines.map((m) => (
              <li key={m.id}>
                <Link
                  to={'/settings/machines' as never}
                  className="lk-fact"
                  aria-label={`${loadLine(m)}. Unlock to see which runs.`}
                >
                  <span className="lk-fact__icon">
                    <Glyph kind="run" />
                  </span>
                  <span className="lk-fact__title">{loadLine(m)}</span>
                  <ChevronRight size={16} aria-hidden="true" className="lk-fact__chev" />
                </Link>
              </li>
            ))}
          </ul>
        </div>
      ) : null}
    </section>
  )
}

/**
 * After an unlock that needed a machine, offer ONCE to set up the passkey (Face ID) unlock, so
 * the next unlock is the passkey alone. Inline, dismissible, remembered per browser.
 */
export function UnlockOffer() {
  const b = useBackend()
  const state = useContentLock()
  const [phase, setPhase] = useState<'idle' | 'busy' | 'done' | 'unsupported' | 'error'>('idle')
  const offering = state.status === 'unlocked' && state.offerProtect
  if (!offering && phase !== 'done') return null
  const faceId = /iPhone|iPad|Macintosh/i.test(globalThis.navigator?.userAgent ?? '')
  const what = faceId ? 'Face ID' : 'your passkey'
  if (phase === 'done')
    return (
      <p className="lk-offer lk-offer--done" role="status">
        Done: next time {what} unlocks {deviceNoun()} by itself.
      </p>
    )
  return (
    <section className="lk-offer" aria-label="Unlock faster next time" data-testid="unlock-offer">
      <span className="lk-offer__icon">
        <ScanFace size={16} aria-hidden="true" />
      </span>
      <div className="lk-offer__text">
        <p className="lk-offer__title">Unlock with {what} next time?</p>
        <p className="lk-offer__meta">
          {phase === 'unsupported'
            ? 'This passkey can’t do that, so your Mac keeps unlocking this browser.'
            : phase === 'error'
              ? 'That didn’t work. Try again, or set it up later in You › Security.'
              : `Then ${deviceNoun()} won’t need your Mac to unlock: one more passkey tap now.`}
        </p>
      </div>
      <div className="lk-offer__actions">
        {phase !== 'unsupported' ? (
          <Button
            variant="primary"
            size="sm"
            disabled={phase === 'busy'}
            onClick={() => {
              setPhase('busy')
              void b.lock
                ?.protect()
                .then((r) => setPhase(r.supported ? 'done' : 'unsupported'))
                .catch(() => setPhase('error'))
            }}
          >
            {phase === 'busy' ? 'Waiting for passkey…' : `Set up ${what}`}
          </Button>
        ) : null}
        <button
          type="button"
          className="vk-iconbtn"
          aria-label="Not now"
          onClick={() => b.lock?.dismissOffer()}
        >
          <X size={16} aria-hidden="true" />
        </button>
      </div>
    </section>
  )
}
